if (!customElements.get('dynamic-pricing-select')) {
  customElements.define(
    'dynamic-pricing-select',
    class DynamicPricingSelect extends HTMLElement {
      constructor() {
        super();
        this.select = this.querySelector('.days-select');
        this.list = this.querySelector('ul');
        this.overlay = this.querySelector('.days-select-overlay');
        this.closeBtn = this.querySelector('.days-select-close');
        this.discount = Number(this.dataset.disc) || 0;
        this.isOnSale = this.discount > 0;
        this.quantity = 1;
        this.variantId = this.dataset.variantId;
        this.params = Number(new URLSearchParams(window.location.search).get('days'));
        const days = Number(this.params) || window.MIN_DAYS;
        this.days = Math.min(window.MAX_DAYS, Math.max(window.MIN_DAYS, days));
        this.select.querySelector('p').innerHTML = this.days;

        this.handleSelectClick = this.handleSelectClick.bind(this);
        this.handleCloseClick = this.handleCloseClick.bind(this);
        this.handleOverlayClick = this.handleOverlayClick.bind(this);
        this.handleEscapeKey = this.handleEscapeKey.bind(this);
        this.handleDaySelectBound = this.handleDaySelect.bind(this);
        this.handleQuantityChangeBound = this.handleQuantityChangeBound.bind(this);
        this.handleQuantityResetBound = this.handleQuantityResetBound.bind(this);
      }

      connectedCallback() {
        const init = () => {
          this.calculatePrice();
          this.updateProductFormInputs();
          this.isMobile();

          this.select.addEventListener('click', this.handleSelectClick);
          this.closeBtn.addEventListener('click', this.handleCloseClick);

          this.overlay.addEventListener('click', this.handleOverlayClick);
          document.addEventListener('keydown', this.handleEscapeKey);
          document.addEventListener('click', this.handleDocumentClick.bind(this));

          this.handleQuantityChange();
          this.displayOptions();
        };

        this.initCallback = init;
        document.readyState === 'loading'
          ? document.addEventListener('DOMContentLoaded', this.initCallback)
          : init();
      }

      updateQueryStringParam() {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('days', this.days);
        history.replaceState(null, null, `?${searchParams.toString()}`);
      }

      isMobile() {
        return window.matchMedia('(max-width: 768px)').matches;
      }

      displayOptions() {
        const fragment = document.createDocumentFragment();

        let i = 1;
        while (i <= window.MAX_DAYS) {
          const li = document.createElement('li');
          li.value = i;
          li.innerHTML = i;
          li.classList.add('dp-option');
          li.value === this.days && li.classList.add('selected');
          fragment.appendChild(li);
          i++;
        }

        this.list.appendChild(fragment);

        this.options = this.querySelectorAll('.dp-option');
        this.options.forEach((option) => {
          option.addEventListener('click', this.handleDaySelectBound);
        });
      }

      handleSelectClick() {
        if (this.classList.contains('is-open')) {
          this.closePopup();
        } else {
          this.openPopup();
        }
      }

      handleDocumentClick(e) {
        if (!this.contains(e.target)) {
          this.closePopup();
        }
      }

      handleCloseClick() {
        this.closePopup();
      }

      handleOverlayClick(e) {
        if (e.target === this.overlay) {
          this.closePopup();
        }
      }

      handleEscapeKey(e) {
        if (e.key === 'Escape') {
          this.closePopup();
        }
      }

      handleDaySelect(e) {
        this.options.forEach((option) => {
          option.classList.remove('selected');
        });
        e.target.classList.add('selected');
        this.days = Number(e.target.innerHTML);
        this.select.querySelector('p').innerHTML = this.days;

        this.calculatePrice();
        this.updateProductFormInputs();

        clearTimeout(this.closeTimeout);
        this.closeTimeout = setTimeout(() => {
          this.closePopup();
        }, 150);
      }

      scrollSelectedOptionIntoView() {
        if (!this.list) return;
        const selectedOption =
          this.list.querySelector('.dp-option.selected') ||
          this.list.querySelector(`.dp-option[value="${this.days}"]`);

        if (!selectedOption) return;

        const listRect = this.list.getBoundingClientRect();
        const optionRect = selectedOption.getBoundingClientRect();
        const offset = optionRect.top - listRect.top;
        const targetScroll =
          this.list.scrollTop +
          offset -
          (this.list.clientHeight / 2 - selectedOption.clientHeight / 2);

        this.list.scrollTop = Math.max(0, targetScroll);
      }

      openPopup() {
        if (this.isMobile()) {
          document.body.appendChild(this.overlay);
          window.scrollLock?.lock();
        }

        this.overlay.classList.remove('closing');
        this.overlay.classList.remove('active');
        // Allow initial styles to apply before transitioning.
        requestAnimationFrame(() => {
          this.overlay.classList.add('active');
          // if (this.isMobile()) {
          requestAnimationFrame(() => {
            this.scrollSelectedOptionIntoView();
          });
          // }
        });
        this.classList.add('is-open');
      }

      closePopup() {
        if (!this.overlay.classList.contains('active')) return;
        const isMobile = this.isMobile();

        this.overlay.classList.remove('active');
        this.overlay.classList.add('closing');
        this.classList.remove('is-open');

        if (isMobile) {
          window.scrollLock?.unlock();
        }

        clearTimeout(this.closeAnimationTimeout);
        this.closeAnimationTimeout = setTimeout(() => {
          this.overlay.classList.remove('closing');
          if (isMobile) {
            this.appendChild(this.overlay);
          }
        }, 240);
      }

      handleQuantityChange() {
        this.quantitySelector = document.querySelector('quantity-input input[name="quantity"]');

        if (!this.quantitySelector) return;

        this.quantitySelector.addEventListener('change', this.handleQuantityChangeBound);
        this.quantitySelector.addEventListener('inputReset', this.handleQuantityResetBound);
      }

      handleQuantityChangeBound() {
        this.quantity = Number(this.quantitySelector?.value || 1);
        this.calculatePrice();
      }

      handleQuantityResetBound() {
        this.quantity = 1;
        this.calculatePrice();
      }

      formatPrice(currency, val) {
        const formattedPrice = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol',
        });
        return formattedPrice.format(val).replace(/\.00$/, '');
      }

      calculatePrice() {
        this.updateQueryStringParam();

        const totalPriceContainer = document.querySelector('.total-dp-price');
        const price = getPrice(this.days);

        if (!price || !totalPriceContainer) return;
        totalPriceContainer.innerHTML = `${window.cartCurrency.slice(0, 2)}${this.formatPrice(window.cartCurrency, price * this.quantity)}`;

        this.calculatePerDayPrice(price);

        if (this.isOnSale) {
          const salePriceSpan = document.querySelector('.total-dp-price-sale');
          const salePrice = price - (price * this.discount) / 100;
          salePriceSpan.innerHTML = `${window.cartCurrency.slice(0, 2)}${this.formatPrice(window.cartCurrency, salePrice * this.quantity)}`;
        } else {
        }
      }

      calculatePerDayPrice(totalPrice) {
        const perDayPriceContainer = document.querySelector('.perday-dp-price');
        const perDayPriceSaleContainer = document.querySelector('.perday-dp-price-sale');

        if (!perDayPriceContainer || !perDayPriceSaleContainer) return;
        if (this.isOnSale) {
          const multiplier = (100 - this.discount) / 100;
          perDayPriceContainer.innerHTML = `${this.formatPrice(window.cartCurrency, (totalPrice * multiplier) / this.days)}/${this.quantity > 1 ? 'eSIM/' : ''}day`;
        } else {
          perDayPriceContainer.innerHTML = `${this.formatPrice(window.cartCurrency, totalPrice / this.days)}/${this.quantity > 1 ? 'eSIM/' : ''}day`;
        }
      }

      updateProductFormInputs() {
        const variantIdInput = document.querySelector('input[name="id"]');
        const dpDaysInput = document.querySelector('input[name="properties[dp-days]"]');

        if (variantIdInput && dpDaysInput) {
          variantIdInput.value = this.variantId;
          dpDaysInput.value = this.days;
        }
      }

      disconnectedCallback() {
        clearTimeout(this.closeTimeout);
        clearTimeout(this.closeAnimationTimeout);

        if (this.initCallback) {
          document.removeEventListener('DOMContentLoaded', this.initCallback);
        }

        this.select?.removeEventListener('click', this.handleSelectClick);
        this.closeBtn?.removeEventListener('click', this.handleCloseClick);
        this.overlay?.removeEventListener('click', this.handleOverlayClick);
        document.removeEventListener('keydown', this.handleEscapeKey);
        document.removeEventListener('click', this.handleDocumentClick.bind(this));

        if (this.options?.length) {
          this.options.forEach((option) => {
            option.removeEventListener('click', this.handleDaySelectBound);
          });
        }

        if (this.quantitySelector) {
          this.quantitySelector.removeEventListener('change', this.handleQuantityChangeBound);
          this.quantitySelector.removeEventListener('inputReset', this.handleQuantityResetBound);
        }

        if (this.overlay?.parentElement === document.body) {
          this.appendChild(this.overlay);
        }
      }
    },
  );
}
